import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { slipDebug } from "./paymentSlipDebug";

const EMAIL_MAX_CANVAS_WIDTH_PX = 1100;
const EMAIL_JPEG_QUALITY = 0.82;

function getFileLabel({ bookingBarcodeNumber, recordSerialNo } = {}) {
  return recordSerialNo || bookingBarcodeNumber || "receipt";
}

function resizeCanvasIfNeeded(source, maxWidth) {
  if (source.width <= maxWidth) return source;

  const scale = maxWidth / source.width;
  const dest = document.createElement("canvas");
  dest.width = maxWidth;
  dest.height = Math.round(source.height * scale);

  const ctx = dest.getContext("2d");
  if (!ctx) return source;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, dest.width, dest.height);
  ctx.drawImage(source, 0, 0, dest.width, dest.height);
  return dest;
}

/** One continuous page — avoids black bars from multi-page image offset slicing. */
function buildPdfBlobFromCanvas(canvas, { imageFormat, jpegQuality }) {
  const isJpeg = imageFormat === "JPEG";
  const imgData = isJpeg
    ? canvas.toDataURL("image/jpeg", jpegQuality)
    : canvas.toDataURL("image/png");

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "px",
    format: [canvas.width, canvas.height],
  });

  pdf.addImage(
    imgData,
    isJpeg ? "JPEG" : "PNG",
    0,
    0,
    canvas.width,
    canvas.height,
    undefined,
    "FAST",
  );

  return pdf.output("blob");
}

async function waitForImages(element) {
  const images = Array.from(element.querySelectorAll("img"));
  await Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        }),
    ),
  );
}

async function captureReceiptCanvas(element, scale) {
  await waitForImages(element);

  return html2canvas(element, {
    scale,
    useCORS: true,
    allowTaint: true,
    backgroundColor: "#ffffff",
    scrollY: -window.scrollY,
    logging: false,
    imageTimeout: 15000,
    onclone: (_doc, clonedElement) => {
      clonedElement.style.boxShadow = "none";
      clonedElement.style.backgroundImage = "none";
      clonedElement.querySelectorAll(".shadow-lg, .shadow").forEach((el) => {
        el.style.boxShadow = "none";
      });
    },
  });
}

/** Full-quality PDF for local download. */
export async function buildPaymentSlipPdfBlob(
  element,
  { bookingBarcodeNumber, recordSerialNo } = {},
) {
  if (!element) {
    slipDebug("buildPaymentSlipPdfBlob", "STOP — no element");
    throw new Error("Payment slip element is not ready.");
  }

  slipDebug("buildPaymentSlipPdfBlob", "START html2canvas (download quality)");
  const canvas = await captureReceiptCanvas(element, 2);
  const blob = buildPdfBlobFromCanvas(canvas, { imageFormat: "PNG" });
  const fileName = `Payment_Receipt_${getFileLabel({ bookingBarcodeNumber, recordSerialNo })}.pdf`;

  slipDebug("buildPaymentSlipPdfBlob", "DONE", {
    fileName,
    blobSize: blob?.size,
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
  });

  return { blob, fileName };
}

/** Smaller PDF for email / UploadPaymentSlip API (JPEG, single continuous page). */
export async function buildPaymentSlipPdfBlobForEmail(
  element,
  { bookingBarcodeNumber, recordSerialNo } = {},
) {
  if (!element) {
    slipDebug("buildPaymentSlipPdfBlobForEmail", "STOP — no element");
    throw new Error("Payment slip element is not ready.");
  }

  slipDebug("buildPaymentSlipPdfBlobForEmail", "START html2canvas (email quality)");
  const rawCanvas = await captureReceiptCanvas(element, 1);
  const canvas = resizeCanvasIfNeeded(rawCanvas, EMAIL_MAX_CANVAS_WIDTH_PX);
  const blob = buildPdfBlobFromCanvas(canvas, {
    imageFormat: "JPEG",
    jpegQuality: EMAIL_JPEG_QUALITY,
  });
  const fileName = `Payment_Receipt_${getFileLabel({ bookingBarcodeNumber, recordSerialNo })}.pdf`;

  slipDebug("buildPaymentSlipPdfBlobForEmail", "DONE", {
    fileName,
    blobSize: blob?.size,
    blobSizeMb: (blob.size / (1024 * 1024)).toFixed(2),
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
  });

  return { blob, fileName };
}

export function downloadPdfBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Open a PDF blob and trigger the browser print dialog. */
export function printPdfBlob(blob) {
  if (!blob) {
    throw new Error("PDF blob is missing.");
  }

  const url = URL.createObjectURL(blob);

  // Prefer a real window so the browser PDF viewer can print reliably.
  const win = window.open(url, "_blank");
  if (win) {
    const tryPrint = () => {
      try {
        win.focus();
        win.print();
      } catch {
        // PDF plugin may not support scripted print yet; user can still print manually.
      }
    };
    win.addEventListener("load", () => setTimeout(tryPrint, 300));
    // Chrome PDF viewer often does not fire load — retry shortly after open.
    setTimeout(tryPrint, 1200);
    setTimeout(() => URL.revokeObjectURL(url), 120_000);
    return;
  }

  // Pop-up blocked: fall back to a hidden iframe.
  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", "Print invoice");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "1px";
  iframe.style.height = "1px";
  iframe.style.opacity = "0";
  iframe.style.pointerEvents = "none";
  iframe.style.border = "0";
  iframe.src = url;
  document.body.appendChild(iframe);

  const cleanup = () => {
    iframe.remove();
    URL.revokeObjectURL(url);
  };

  const triggerPrint = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      setTimeout(cleanup, 60_000);
    }
  };

  iframe.onload = () => setTimeout(triggerPrint, 250);
  setTimeout(triggerPrint, 1200);
}
